import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { LogoMark } from "@/components/site/Logo";
import { ShowsTab } from "@/components/admin/ShowsTab";
import { MembersTab } from "@/components/admin/MembersTab";
import { GalleryTab } from "@/components/admin/GalleryTab";
import { ListTab } from "@/components/admin/ListTab";

const TABS = [
  ["shows", "Shows"], ["founders", "Founders"], ["members", "Crew"], ["hosts", "Hosts"], ["gallery", "Gallery"], ["vips", "VIP members"], ["inquiries", "Inquiries"], ["subscribers", "Mailing list"],
];

export default function Admin() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({});
  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => nav("/admin/login"));
    api.get("/admin/stats").then((r) => setStats(r.data || {})).catch(() => {});
  }, [nav]);
  const logout = async () => { await api.post("/auth/logout").catch(() => {}); nav("/admin/login"); };
  if (!user) return <div className="min-h-screen grid place-items-center font-mono text-xs uppercase tracking-[0.2em]">Loading…</div>;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen px-5 md:px-10 py-8">
      <header className="flex items-center justify-between gap-4 border-b border-white/10 pb-6">
        <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link"><LogoMark /><span className="font-display uppercase text-2xl">Crew HQ</span></Link>
        <div className="flex items-center gap-4">
          <span className="hidden sm:inline font-mono text-xs text-zinc-400">{user.email}</span>
          <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] border border-white/20 px-3 py-2 hover:border-[#56ED4A]"><LogOut size={14} /> Out</button>
        </div>
      </header>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 my-8">
        {[["shows", "Upcoming shows"], ["members", "Crew"], ["vips", "VIPs"], ["inquiries", "Inquiries"], ["subscribers", "Subscribers"]].map(([k, l]) => (
          <div key={k} data-testid={`admin-stat-${k}`} className="border border-white/10 bg-[#121216] p-5">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{l}</div>
            <div className="font-display text-5xl mt-2">{stats?.[k] ?? 0}</div>
          </div>
        ))}
      </div>
      <Tabs defaultValue="shows">
        <TabsList className="flex flex-wrap h-auto justify-start bg-transparent gap-2 p-0">
          {TABS.map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="font-mono text-xs uppercase tracking-[0.2em] border border-white/20 px-4 py-2 data-[state=active]:bg-[#56ED4A] data-[state=active]:text-black">{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="shows" className="mt-6"><ShowsTab /></TabsContent>
        <TabsContent value="founders" className="mt-6"><MembersTab section="founder" /></TabsContent>
        <TabsContent value="members" className="mt-6"><MembersTab section="crew" /></TabsContent>
        <TabsContent value="hosts" className="mt-6"><MembersTab section="host" /></TabsContent>
        <TabsContent value="gallery" className="mt-6"><GalleryTab /></TabsContent>
        <TabsContent value="vips" className="mt-6"><ListTab path="/admin/vips" cols={[["code", "Code"], ["email", "Email"], ["name", "Name"], ["created_at", "Joined"], ["expires_at", "Expires"]]} testId="vips" /></TabsContent>
        <TabsContent value="inquiries" className="mt-6"><ListTab path="/admin/inquiries" cols={[["kind", "Type"], ["name", "Name"], ["email", "Email"], ["message", "Message"], ["created_at", "Received"]]} testId="inquiries" /></TabsContent>
        <TabsContent value="subscribers" className="mt-6"><ListTab path="/admin/subscribers" cols={[["email", "Email"], ["created_at", "Joined"]]} testId="subscribers" /></TabsContent>
      </Tabs>
    </div>
  );
}
